import React, { useState } from 'react';
import { Download, RefreshCw, AlertCircle } from 'lucide-react';

const DownloadButton = ({
  url, 
  label, 
  color = "blue", 
  icon = <Download className="mr-1 h-4 w-4" />, 
  className = ""
}) => {
  const [downloadStatus, setDownloadStatus] = useState('idle');
  
  const handleDownload = async () => {
    try {
      setDownloadStatus('downloading');
      
      // Try the fetch API approach first
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      // Check content type to ensure we got a CSV
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // We got JSON (probably an error) instead of CSV
        const errorData = await response.json();
        console.error('Error downloading CSV:', errorData);
        setDownloadStatus('error');
        setTimeout(() => setDownloadStatus('idle'), 3000);
        return;
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create object URL
      const objectUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element and trigger the download
      const link = document.createElement('a');
      link.href = objectUrl;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'download.csv';
      if (contentDisposition) {
        // Updated regex to properly extract the filename without trailing underscores
        const filenameMatch = contentDisposition.match(/filename="?([^"_]+(?:\.csv))"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        } else {
          // Fallback to extract any filename ending with .csv
          const fallbackMatch = contentDisposition.match(/filename="?([^"]+\.csv)"?/i);
          if (fallbackMatch) {
            filename = fallbackMatch[1];
          }
        }
      }
      
      // Ensure the filename ends with .csv (not .csv_)
      if (filename.endsWith('.csv_')) {
        filename = filename.slice(0, -1);
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setDownloadStatus('success');
      setTimeout(() => setDownloadStatus('idle'), 2000);
      
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      setTimeout(() => setDownloadStatus('idle'), 3000);
      
      // Fallback to direct link approach
      try {
        console.log('Trying fallback download method for:', url);
        window.open(url, '_blank');
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);
      }
    }
  };

  // Color variants for different states
  const colorVariants = {
    blue: {
      idle: 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50',
      downloading: 'bg-blue-50 text-blue-700 border-blue-300 opacity-50',
      success: 'bg-green-50 text-green-700 border-green-300',
      error: 'bg-red-50 text-red-700 border-red-300'
    },
    gray: {
      idle: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
      downloading: 'bg-gray-50 text-gray-700 border-gray-300 opacity-50',
      success: 'bg-green-50 text-green-700 border-green-300',
      error: 'bg-red-50 text-red-700 border-red-300'
    },
    green: {
      idle: 'bg-white text-green-700 border-green-300 hover:bg-green-50',
      downloading: 'bg-green-50 text-green-700 border-green-300 opacity-50',
      success: 'bg-green-50 text-green-700 border-green-300',
      error: 'bg-red-50 text-red-700 border-red-300'
    },
    red: {
      idle: 'bg-white text-red-700 border-red-300 hover:bg-red-50',
      downloading: 'bg-red-50 text-red-700 border-red-300 opacity-50',
      success: 'bg-green-50 text-green-700 border-green-300',
      error: 'bg-red-50 text-red-700 border-red-300'
    }
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation(); // Prevent triggering parent onClick (card toggle)
        handleDownload();
      }}
      disabled={downloadStatus === 'downloading'}
      className={`
        px-2 py-1 text-xs flex items-center rounded border 
        transition-all duration-200 ease-in-out
        ${colorVariants[color][downloadStatus]}
        ${className}
        ${downloadStatus === 'downloading' ? 'cursor-wait' : 'cursor-pointer'}
      `}
    >
      {downloadStatus === 'downloading' ? (
        <RefreshCw className="mr-1 h-4 w-4 animate-spin" />
      ) : downloadStatus === 'error' ? (
        <AlertCircle className="mr-1 h-4 w-4" />
      ) : downloadStatus === 'success' ? (
        <div className="mr-1">✓</div>
      ) : (
        icon
      )}
      {downloadStatus === 'downloading' ? 'Downloading...' : 
       downloadStatus === 'error' ? 'Failed' :
       downloadStatus === 'success' ? 'Success!' : label}
    </button>
  );
};

export default DownloadButton;